import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Upload, Save, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { api, setToken, getToken, resolvePhoto } from "@/lib/api";

const inputCls =
  "min-h-11 w-full rounded-lg border border-[var(--wf-border)] bg-[var(--wf-paper)] px-3.5 py-2.5 text-sm text-[var(--wf-ink)] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const TopBar = () => (
  <header className="border-b border-[var(--wf-border)] bg-[var(--wf-paper)]">
    <div className="wf-container flex h-16 items-center justify-between">
      <Link to="/" className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <img src="/images/logo-maroon.png" alt="World Farmers" className="h-9 w-auto" />
        <span className="text-sm font-semibold text-[var(--wf-soil)]">Staff Portal</span>
      </Link>
      <Link to="/who-we-are" className="text-sm font-semibold text-[var(--wf-maroon)] hover:underline" data-testid="admin-view-site-link">View team page →</Link>
    </div>
  </header>
);

const LoginView = ({ onLoggedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      onLoggedIn(data.member);
      toast.success(`Welcome, ${data.member.name.split(" ")[0]}!`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-16 max-w-md px-4">
      <div className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-8 shadow-[var(--wf-shadow-sm)]">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]"><ShieldCheck className="h-6 w-6" aria-hidden="true" /></span>
        <h1 className="mt-4 font-serif text-2xl font-semibold text-[var(--wf-ink)]">Staff sign in</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to update your own profile.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="a-email" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Email</label>
            <input id="a-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="name@worldfarmers.org" data-testid="admin-login-email" autoComplete="username" />
          </div>
          <div>
            <label htmlFor="a-pw" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Password</label>
            <input id="a-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-login-password" autoComplete="current-password" />
          </div>
          {error && <p className="text-sm text-[var(--wf-maroon)]" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--wf-maroon)] px-6 py-3 text-sm font-semibold text-[var(--wf-paper)] transition-colors hover:bg-[var(--wf-maroon-dark)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="admin-login-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Sign in
          </button>
        </form>
        <div className="mt-6 rounded-sm bg-[var(--wf-cream)] p-4 text-xs text-muted-foreground">
          <p className="font-semibold text-[var(--wf-soil)]">Demo account</p>
          <p className="mt-1">Email: <code className="text-[var(--wf-maroon)]">amie-avila@worldfarmers.org</code></p>
          <p>Password: <code className="text-[var(--wf-maroon)]">worldfarmers</code></p>
        </div>
      </div>
    </div>
  );
};

const EditorView = ({ member, setMember, onLogout }) => {
  const [name, setName] = useState(member.name);
  const [role, setRole] = useState(member.role);
  const [bio, setBio] = useState(member.bio);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { name, role, bio });
      setMember(data);
      toast.success("Profile saved", { description: "Your changes are live on the Who We Are page." });
    } catch (err) {
      toast.error("Could not save", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/photo", fd);
      setMember(data);
      toast.success("Photo updated");
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || "Please try a different image." });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const photoSrc = resolvePhoto(member.photo);

  return (
    <div className="mx-auto my-12 max-w-3xl px-4" data-testid="admin-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">Edit your profile</h1>
          <p className="mt-1 text-sm text-muted-foreground">{member.role}</p>
        </div>
        <button type="button" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-[var(--wf-border)] px-4 py-2 text-sm font-semibold text-[var(--wf-soil)] hover:bg-[var(--wf-cream)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="admin-logout-button">
          <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-[200px_1fr]">
        {/* Photo */}
        <div>
          <div className="aspect-[4/5] w-full overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)]">
            {photoSrc ? (
              <img src={photoSrc} alt="Your current portrait" className="h-full w-full object-cover object-top" data-testid="admin-photo-preview" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[var(--wf-farm-green)] font-serif text-4xl font-semibold text-white">
                {member.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("")}
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="sr-only" data-testid="admin-photo-input" id="admin-photo-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--wf-maroon)] px-4 py-2.5 text-sm font-semibold text-[var(--wf-maroon)] transition-colors hover:bg-[var(--wf-maroon)] hover:text-white disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="admin-photo-upload-button">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />} Upload photo
          </button>
        </div>

        {/* Fields */}
        <div className="space-y-4">
          <div>
            <label htmlFor="e-name" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Name</label>
            <input id="e-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="admin-name-input" />
          </div>
          <div>
            <label htmlFor="e-role" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Role / title</label>
            <input id="e-role" value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="admin-role-input" />
          </div>
          <div>
            <label htmlFor="e-bio" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Bio</label>
            <textarea id="e-bio" rows={10} value={bio} onChange={(e) => setBio(e.target.value)} className={`${inputCls} resize-y`} data-testid="admin-bio-input" />
          </div>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--wf-maroon)] px-6 py-3 text-sm font-semibold text-[var(--wf-paper)] transition-colors hover:bg-[var(--wf-maroon-dark)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="admin-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [member, setMember] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!getToken()) { setChecking(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setMember(data);
      } catch {
        setToken(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const logout = () => { setToken(null); setMember(null); };

  return (
    <div className="min-h-screen bg-[var(--wf-cream)]">
      <TopBar />
      {checking ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" /></div>
      ) : member ? (
        <EditorView member={member} setMember={setMember} onLogout={logout} />
      ) : (
        <LoginView onLoggedIn={setMember} />
      )}
      <Toaster />
    </div>
  );
}
